import {
  calcdeckUtilityRegistry,
  type RouteKey,
} from '@/shared/utility-registry'

interface ToolCardPresentation {
  symbol: string
  formula: string
  description: string
  markerClassName: string
}

const toolCardPresentation: Partial<Record<RouteKey, ToolCardPresentation>> = {
  'percentage-calculator': {
    symbol: '%',
    formula: 'X% от Y',
    description: 'Процент от числа, доля и изменение значения.',
    markerClassName: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/60 dark:text-blue-200',
  },
  'average-median-calculator': {
    symbol: 'x̄',
    formula: 'x̄ · Me',
    description: 'Среднее, медиана и сводка по списку чисел.',
    markerClassName: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-200',
  },
  'fraction-calculator': {
    symbol: '⅗',
    formula: 'a⁄b ± c⁄d',
    description: 'Точные действия, сокращение и смешанные числа.',
    markerClassName: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/60 dark:text-violet-200',
  },
  'combinatorics-calculator': {
    symbol: 'C',
    formula: 'C(n, k)',
    description: 'Сочетания и размещения с точным результатом.',
    markerClassName: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200',
  },
  'vat-calculator': {
    symbol: 'НДС',
    formula: '₽ + %',
    description: 'Начисление, выделение и сумма прописью для документа.',
    markerClassName: 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950/60 dark:text-teal-200',
  },
  'unit-converter': {
    symbol: '↔',
    formula: 'см ↔ м',
    description: 'Единицы длины, массы, площади, объёма и не только.',
    markerClassName: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/60 dark:text-sky-200',
  },
  'ratio-proportion-calculator': {
    symbol: '∶',
    formula: 'A:B = C:X',
    description: 'Пропорции, сокращение и масштабирование отношений.',
    markerClassName: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200',
  },
  'margin-markup-calculator': {
    symbol: '₽',
    formula: '₽ · %',
    description: 'Цена, прибыль, маржа и наценка по двум значениям.',
    markerClassName: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
  },
  'discount-calculator': {
    symbol: '−%',
    formula: '−20%',
    description: 'Цена после скидки, выгода и обратный расчёт.',
    markerClassName: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-200',
  },
  'random-number-generator': {
    symbol: '#',
    formula: 'min…max',
    description: 'Числа в диапазоне с повторами или без.',
    markerClassName: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-800 dark:bg-fuchsia-950/60 dark:text-fuchsia-200',
  },
  'dice-roller': {
    symbol: 'd20',
    formula: '3d6+2',
    description: 'Броски d4–d100 и формулы с модификатором.',
    markerClassName: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/60 dark:text-orange-200',
  },
  'coin-flipper': {
    symbol: '○',
    formula: '○ / ●',
    description: 'Один бросок или серия с подсчётом сторон.',
    markerClassName: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-200',
  },
}

const fallbackPresentation: ToolCardPresentation = {
  symbol: '=',
  formula: 'a → b',
  description: 'Быстрый точный расчёт прямо в браузере.',
  markerClassName: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/60 dark:text-blue-200',
}

export function IndexPage() {
  const tools = calcdeckUtilityRegistry.filter((route) => route.availability === 'available' && !('hidden' in route.nav && route.nav.hidden))

  return (
    <div className="mx-auto max-w-7xl">
      <section className="pb-4 pt-1 sm:pb-6">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Калькуляторы онлайн</h1>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground sm:text-base sm:leading-7">
          Проценты, НДС, пропорции, комбинации, кубики и конвертеры единиц. Считается в браузере, без регистрации.
        </p>
      </section>

      <section id="tools" className="grid scroll-mt-24 gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label="Все калькуляторы">
        {tools.map((tool) => {
          const presentation = toolCardPresentation[tool.routeKey] ?? fallbackPresentation

          return (
            <a
              key={tool.routeKey}
              href={tool.path}
              data-catalog-card={tool.routeKey}
              className="group flex items-start gap-3 rounded-lg border bg-card p-4 shadow-sm transition-colors hover:border-primary/45 hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <span
                aria-hidden="true"
                className={`flex size-11 shrink-0 items-center justify-center rounded-lg border font-mono text-sm font-bold ${presentation.markerClassName}`}
              >
                {presentation.symbol}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2">
                  <h2 className="text-base font-semibold tracking-tight transition-colors group-hover:text-primary">
                    {tool.nav.label}
                  </h2>
                  <span aria-hidden="true" className="font-mono text-xs text-muted-foreground">
                    {presentation.formula}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {presentation.description}
                </p>
              </div>
            </a>
          )
        })}
      </section>
    </div>
  )
}
